import React, { useEffect, useState } from 'react';
import { BarChart3, TrendingUp, Building2, FileCheck, AlertTriangle, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import { PageHeader, Card, LoadingSpinner } from '../components/UI';

function BarChart({ data, valueKey, labelKey, color = 'var(--primary)', maxVal }) {
  const max = maxVal || Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 110, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d[labelKey]}</div>
          <div style={{ flex: 1, position: 'relative' }}>
            <div style={{ height: 28, borderRadius: 6, background: 'var(--bg-elevated)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(d[valueKey] / max) * 100}%`, background: color, borderRadius: 6, transition: 'width 0.8s ease', minWidth: d[valueKey] > 0 ? 4 : 0 }} />
            </div>
          </div>
          <div style={{ width: 38, fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', textAlign: 'right', flexShrink: 0 }}>{d[valueKey]}</div>
        </div>
      ))}
    </div>
  );
}

function LineChart({ data }) {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d.total || 0), 1);
  const W = 600, H = 180, PAD = 30;
  const xStep = (W - PAD * 2) / Math.max(data.length - 1, 1);

  const pts = (key, color) => {
    const points = data.map((d, i) => ({
      x: PAD + i * xStep,
      y: H - PAD - ((d[key] || 0) / maxVal) * (H - PAD * 2),
    }));
    const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
    return { points, path, color };
  };

  const lines = [
    pts('total', 'var(--primary)'),
    pts('on_time', 'var(--success)'),
    pts('late', 'var(--danger)'),
  ];

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={W} height={H} style={{ display: 'block', minWidth: 300 }}>
        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map(t => {
          const y = H - PAD - t * (H - PAD * 2);
          return <line key={t} x1={PAD} y1={y} x2={W - PAD} y2={y} stroke="var(--border)" strokeWidth="1" strokeDasharray="4" />;
        })}
        {/* Lines */}
        {lines.map((l, li) => (
          <g key={li}>
            <path d={l.path} fill="none" stroke={l.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            {l.points.map((p, pi) => <circle key={pi} cx={p.x} cy={p.y} r="4" fill={l.color} />)}
          </g>
        ))}
        {/* X labels */}
        {data.map((d, i) => (
          <text key={i} x={PAD + i * xStep} y={H - 4} textAnchor="middle" fontSize="10" fill="var(--text-muted)">{d.month?.slice(5)}</text>
        ))}
      </svg>
      <div style={{ display: 'flex', gap: 20, marginTop: 10, justifyContent: 'center' }}>
        {[['Total', 'var(--primary)'], ['On Time', 'var(--success)'], ['Late', 'var(--danger)']].map(([l, c]) => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
            <div style={{ width: 12, height: 3, borderRadius: 2, background: c }} />{l}
          </div>
        ))}
      </div>
    </div>
  );
}

function DonutChart({ data, totalLabel }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  if (total === 0) return <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No data</div>;
  const R = 60, CX = 70, CY = 70;
  let offset = -Math.PI / 2;
  const slices = data.map(d => {
    const frac = d.value / total;
    const angle = frac * 2 * Math.PI;
    const x1 = CX + R * Math.cos(offset);
    const y1 = CY + R * Math.sin(offset);
    offset += angle;
    const x2 = CX + R * Math.cos(offset);
    const y2 = CY + R * Math.sin(offset);
    const large = frac > 0.5 ? 1 : 0;
    return { ...d, path: `M${CX},${CY} L${x1},${y1} A${R},${R} 0 ${large},1 ${x2},${y2} Z`, frac };
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <svg width="140" height="140" style={{ flexShrink: 0 }}>
        {slices.map((s, i) => <path key={i} d={s.path} fill={s.color} />)}
        <circle cx={CX} cy={CY} r="40" fill="var(--bg-card)" />
        <text x={CX} y={CY + 4} textAnchor="middle" fontSize="13" fontWeight="800" fill="var(--text-primary)">{total}</text>
        <text x={CX} y={CY + 16} textAnchor="middle" fontSize="8" fill="var(--text-muted)">{totalLabel}</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {slices.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
            <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{s.label}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 'auto' }}>{s.value} ({Math.round(s.frac * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillDept, setDrillDept] = useState(null);

  useEffect(() => {
    api.get('/reminders/analytics').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!data) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Failed to load analytics.</div>;

  const freqData = (data.byFreq || []).map(d => ({ label: d.frequency, value: d.count }));
  const totalSubs = (data.complianceByDept || []).reduce((s, d) => s + (d.total_submissions || 0), 0);
  const totalOnTime = (data.complianceByDept || []).reduce((s, d) => s + (d.on_time || 0), 0);
  const globalCompliance = totalSubs > 0 ? Math.round((totalOnTime / totalSubs) * 100) : null;

  const complianceColor = (rate) => rate >= 80 ? 'var(--success)' : rate >= 60 ? 'var(--warning)' : 'var(--danger)';

  return (
    <div style={{ padding: '32px 36px', maxWidth: 1280, margin: '0 auto' }} className="fade-in">
      <PageHeader title="Analytics & Insights" subtitle="Organisation-wide compliance reporting and trends" />

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Total Submissions', value: totalSubs, color: 'var(--primary)', icon: FileCheck },
          { label: 'On-Time', value: totalOnTime, color: 'var(--success)', icon: TrendingUp },
          { label: 'Late', value: totalSubs - totalOnTime, color: 'var(--danger)', icon: AlertTriangle },
          { label: 'Compliance Rate', value: globalCompliance !== null ? `${globalCompliance}%` : '—', color: globalCompliance !== null ? complianceColor(globalCompliance) : 'var(--text-muted)', icon: BarChart3 },
        ].map(({ label, value, color, icon: Icon }) => (
          <Card key={label} style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -18, right: -18, width: 70, height: 70, borderRadius: '50%', background: color, opacity: 0.08 }} />
            <div style={{ width: 36, height: 36, borderRadius: 9, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <Icon size={17} color={color} />
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color, letterSpacing: '-0.03em', lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5, fontWeight: 600 }}>{label}</div>
          </Card>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Submission trend */}
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 18 }}>12-Month Submission Trend</h3>
          {data.submissionTrend?.length > 0 ? <LineChart data={data.submissionTrend} /> : <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No submission data yet.</div>}
        </Card>

        {/* Frequency distribution */}
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 18 }}>Reports by Frequency</h3>
          <DonutChart
            data={freqData.map((d, i) => ({
              ...d,
              color: ['var(--primary)','var(--success)','var(--warning)','var(--danger)','var(--info)','var(--accent-2)','#f97316'][i % 7],
            }))}
            totalLabel="reports"
          />
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Reports by dept */}
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 18 }}>Reports by Department</h3>
          {data.byDept?.length > 0
            ? <BarChart data={data.byDept} valueKey="total" labelKey="department" color="var(--primary)" />
            : <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data.</div>
          }
        </Card>

        {/* Top reports by reminders sent */}
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 18 }}>Most Active Reports</h3>
          {data.topReports?.length > 0
            ? <BarChart data={data.topReports} valueKey="reminders_sent" labelKey="report_name" color="var(--accent-2)" />
            : <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data.</div>
          }
        </Card>
      </div>

      {/* Department compliance drill-down */}
      <Card>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 18 }}>Department Compliance</h3>
        {data.complianceByDept?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.complianceByDept.map((d, i) => {
              const rate = d.total_submissions > 0 ? Math.round((d.on_time / d.total_submissions) * 100) : null;
              const isOpen = drillDept === d.department;
              return (
                <div key={i}>
                  <div onClick={() => setDrillDept(isOpen ? null : d.department)} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: 'var(--bg-elevated)', borderRadius: 10, cursor: 'pointer', border: `1.5px solid ${isOpen ? 'var(--primary)' : 'transparent'}`, transition: 'all .15s' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: 'var(--primary-ghost)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Building2 size={16} color="var(--primary)" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>{d.department}</div>
                      <div style={{ height: 8, borderRadius: 99, background: 'var(--border)', overflow: 'hidden', width: '100%' }}>
                        <div style={{ height: '100%', width: `${rate || 0}%`, background: complianceColor(rate || 0), borderRadius: 99, transition: 'width 0.8s ease' }} />
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: complianceColor(rate || 0) }}>{rate !== null ? `${rate}%` : '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{d.total_submissions} submissions</div>
                    </div>
                    <ChevronRight size={16} color="var(--text-muted)" style={{ transition: 'transform .2s', transform: isOpen ? 'rotate(90deg)' : 'none' }} />
                  </div>
                  {isOpen && (
                    <div style={{ padding: '12px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 10px 10px' }}>
                      <div style={{ display: 'flex', gap: 24 }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--success)' }}>{d.on_time || 0}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>On Time</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--danger)' }}>{d.late || 0}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Late</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--primary)' }}>{d.total_submissions || 0}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Total</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No compliance data yet.</div>}
      </Card>
    </div>
  );
}
